import { ColumnDef } from '@tanstack/react-table'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'

import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { Link } from 'react-router-dom'

import { Button } from '@/common/ui/button'
import { SortingArrows } from '@/common/components/SortingArrows'
import { DepositStatus, ManualDepositType } from '@/schema'
import { PlayerNoteColorModal } from '@/modules/manage-player/components/GetPlayerList/components/GetPlayerListTable/CellAction/components/PlayerNoteColorModal'
import { PhoneCell } from '@/common/components/PhoneCell'

import { DepositTransactionResponse } from '../../types'
import DepositActions from './components/DepositActions'
import PlayerBankInfoCell from './components/PlayerBankInfoCell'
import DepositErrorLogsButton from './components/DepositErrorLogsButton'

type DepositData = DepositTransactionResponse['data'][number]

const statusColors: Record<DepositStatus, string> = {
	CREATED: 'text-blue-500',
	PENDING: 'text-yellow-500',
	PROCESSING: 'text-amber-500',
	COMPLETED: 'text-green-500',
	FAILED: 'text-red-500',
	CANCELED: 'text-red-500',
	EXPIRED: 'text-gray-500',
	MANUAL_COMPLETED: 'text-green-500',
	PAYMENT_GATEWAY_FAILED: 'text-red-500',
}

const statusLabels: Record<DepositStatus, string> = {
	CREATED: 'สร้างแล้ว',
	PENDING: 'รอดำเนินการ',
	PROCESSING: 'กำลังตรวจสอบ',
	COMPLETED: 'สำเร็จ',
	FAILED: 'ไม่สำเร็จ',
	CANCELED: 'ยกเลิก',
	EXPIRED: 'หมดอายุ',
	MANUAL_COMPLETED: 'รอ Payment Gateway ตรวจสอบ',
	PAYMENT_GATEWAY_FAILED: 'กรุณาตรวจสอบกับ payment gateway',
}

const methodMap: Record<string, string> = {
	PAYMENT_GATEWAY: 'ธนาคารออโต้',
	SMS_AUTO: 'SMS Auto',
	VERIFY_SLIP: 'Verify Slip',
	AUTOSSLIP: 'Auto Slip',
	TRUE_WALLET: 'True Wallet',
}

export const columns: ColumnDef<DepositData>[] = [
	{
		id: 'playerInfo',
		header: 'ข้อมูลผู้เล่น',
		cell: ({ row }) => {
			const { id, bankTxId, amount, playerBank, player, playerId } =
				row.original
			const color = player?.tags?.[0]?.color
			const handleCopy = () => {
				const details = [
					'รายละเอียดธุรกรรม',
					bankTxId ? `• รหัสรายการ : ${bankTxId}` : null,
					`• รหัสอ้างอิง : ${id}`,
					`• ยอด : ฿${amount.toLocaleString()}`,
					'• ประเภท : ฝาก',
					playerBank?.bankProvider
						? `• ธนาคาร : ${playerBank.bankProvider}`
						: null,
					playerBank?.accountName
						? `• ชื่อบัญชี : ${playerBank.accountName}`
						: null,
					playerBank?.accountNumber
						? `• เลขบัญชี : ${playerBank.accountNumber}`
						: null,
				]
					.filter(Boolean)
					.join('\n')

				navigator.clipboard.writeText(details).then(() => {
					toast.success('คัดลอกสำเร็จ')
				})
			}

			return (
				<div className="space-y-1">
					<div className="flex items-center gap-2">
						<span className="text-xs text-muted-foreground">{id}</span>
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6"
							onClick={handleCopy}
						>
							<Copy className="h-3 w-3" />
						</Button>
					</div>
					{player ? (
						<div className="flex items-center gap-2">
							<PhoneCell playerId={playerId} phone={player.phone} />
							{color && (
								<div
									className="h-4 w-4 shrink-0 rounded-full border border-white shadow-sm"
									style={{ backgroundColor: color }}
									title={`สี: ${color}`}
								/>
							)}
						</div>
					) : (
						<span className="text-muted-foreground">-</span>
					)}
					{player && playerId && (
						<PlayerNoteColorModal
							player={{
								id: playerId,
								phone: player.phone,
								note: player.tags?.[0]?.name,
								color: player.tags?.[0]?.color,
							}}
						/>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'amount',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					จำนวนเงิน
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const promotionName = row.original.promotionName
			const bonusAmount = row.original.bonusAmount
			const manualDepositType = row.original.manualDepositType

			return (
				<div className="space-y-1">
					<span className="font-medium">
						฿{row.original.amount.toLocaleString()}
					</span>
					<p className="text-xs text-muted-foreground">
						โปรโมชั่น: {promotionName || '-'}
					</p>
					<p className="text-xs text-muted-foreground">
						โบนัส: {bonusAmount ? bonusAmount.toLocaleString() : '-'}
					</p>
					<p className="text-xs text-muted-foreground">
						แมนนวล:{' '}
						{manualDepositType
							? manualDepositType === ManualDepositType.REGULAR
								? 'ฝากแมนนวล'
								: 'ฟรีเครดิต'
							: '-'}
					</p>
				</div>
			)
		},
	},
	{
		accessorKey: 'playerBankInfo',
		header: 'ข้อมูลบัญชี',
		cell: ({ row }) => (
			<div className="space-y-1.5">
				<div>
					<p className="text-xs text-muted-foreground">บัญชีผู้เล่น</p>
					<PlayerBankInfoCell row={row} />
				</div>
				<div>
					<p className="text-xs text-muted-foreground">บัญชีรับเงิน</p>
					<p className="text-sm">{row.original.bankInfo || '-'}</p>
				</div>
				<p className="text-xs text-muted-foreground">
					วิธีการ: {methodMap[row.original.method] || 'ธนาคาร'}
				</p>
			</div>
		),
	},
	{
		id: 'statusInfo',
		header: 'สถานะ/ประเภท',
		cell: ({ row }) => {
			const status = row.original.status
			const processor = row.original.processor
			const operator = row.original.manualDepositOperator?.username
			const processorLabel =
				status === DepositStatus.EXPIRED
					? 'หมดอายุ'
					: processor === 'AUTO'
						? `ฝากอัตโนมัติ`
						: `ทำรายการโดยพนักงาน ${operator ? ` ${operator}` : ''}`

			return (
				<div className="space-y-1">
					<span className={statusColors[status] || 'text-gray-500'}>
						{statusLabels[status]}
					</span>
					<p className="text-xs text-muted-foreground">{processorLabel}</p>
				</div>
			)
		},
	},
	{
		accessorKey: 'createdAt',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					วันที่ฝาก
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const note = row.original.note
			const slipRejectReason = (
				row.original as unknown as { slipRejectReason?: string | null }
			).slipRejectReason
			const errorMessage = (
				row.original as unknown as { errorMessage?: string }
			).errorMessage

			const displayNote = slipRejectReason || note || errorMessage

			return (
				<div className="space-y-1">
					<p className="text-sm">
						{format(new Date(row.original.createdAt || ''), 'PPP HH:mm น.', {
							locale: th,
						})}
					</p>
					{displayNote && (
						<p className="max-w-[200px] truncate text-xs text-muted-foreground">
							{displayNote}
						</p>
					)}
				</div>
			)
		},
	},
	{
		id: 'actions',
		header: 'การดำเนินการ',
		cell: ({ row }) => {
			const { id, status, amount, playerId } = row.original
			const errorCount =
				(row.original as { _count?: { paymentGatewayErrors?: number } })._count
					?.paymentGatewayErrors || 0
			return (
				<div className="flex flex-col gap-1.5">
					<DepositActions id={id} status={status} amount={amount} />
					<div className="flex flex-wrap gap-1">
						<Link to={`/operator/transactions/deposits/${id}`}>
							<Button variant="outline" size="sm">
								รายละเอียด
							</Button>
						</Link>
						<Link to={`/operator/get-player-list/player-detail/${playerId}`}>
							<Button variant="outline" size="sm">
								ผู้เล่น
							</Button>
						</Link>
					</div>
					<DepositErrorLogsButton id={id} errorCount={errorCount} />
				</div>
			)
		},
	},
]
